import './shim'
import { createRoot, type Root } from 'react-dom/client'
import coreStyles from './styles/tokens.css?inline'
import widgetStyles from './styles/widget.css?inline'
import { loadGlobalScript } from './lib/loadScript'
import type { ManageBookingMountConfig } from './manage/mountManage'
import { createWidgetRoot, type WidgetRoot } from './shadow'
import { WidgetApp } from './WidgetApp'
import { isDisplayMode, isFlowType, type BookingWidgetConfig, type WidgetEventName } from './types'

export type { BookingWidgetConfig } from './types'
export type { ManageBookingMountConfig } from './manage/mountManage'

const CSS = `${coreStyles}\n${widgetStyles}`

interface MountedWidget {
  root: Root
  widgetRoot: WidgetRoot
  listeners: Map<WidgetEventName, Set<(payload?: unknown) => void>>
  setOpenSignal: (value: number) => void
  setCloseSignal: (value: number) => void
}

const mounted = new Map<HTMLElement, MountedWidget>()
const selectorToElement = new Map<string, HTMLElement>()
const autoMounted = new WeakSet<HTMLElement>()

const AUTO_MOUNT_SELECTOR = '[data-autochain-booking-widget]'

/**
 * Handle returned by mount()/init().
 *
 * Callable so that `const unmount = init(...); unmount()` — the contract the
 * previous version documented and that live embeds rely on — keeps working,
 * while also carrying the imperative controls added here.
 */
export interface WidgetHandle {
  (): void
  open(): void
  close(): void
  destroy(): void
  on(event: WidgetEventName, handler: (payload?: unknown) => void): () => void
}

function resolveElement(target: string | HTMLElement): HTMLElement | null {
  const element = typeof target === 'string' ? document.querySelector(target) : target
  return element instanceof HTMLElement ? element : null
}

/**
 * Mounts the booking widget into a DOM element.
 *
 * The widget renders inside a shadow root, so the host page's CSS cannot
 * reach it and its own styles cannot leak out. No stylesheet link is needed
 * on the host page.
 */
export function mount(target: string | HTMLElement, config: BookingWidgetConfig): WidgetHandle {
  const element = resolveElement(target)
  if (!element) {
    throw new Error(
      `BookingWidget: mount target not found: ${typeof target === 'string' ? target : 'element'}`
    )
  }

  if (mounted.has(element)) unmount(element)

  if (typeof target === 'string') {
    const previous = selectorToElement.get(target)
    if (previous && previous !== element) unmount(previous)
    selectorToElement.set(target, element)
  }

  const widgetRoot = createWidgetRoot(element, CSS)
  const root = createRoot(widgetRoot.container)
  const listeners = new Map<WidgetEventName, Set<(payload?: unknown) => void>>()

  let openSignal = 0
  let closeSignal = 0

  const emit = (name: WidgetEventName, payload?: unknown) => {
    for (const handler of listeners.get(name) ?? []) {
      try {
        handler(payload)
      } catch {
        // A broken host-page handler must not break the widget.
      }
    }
  }

  const render = () => {
    root.render(
      <WidgetApp
        config={config}
        openSignal={openSignal}
        closeSignal={closeSignal}
        onEvent={emit}
      />
    )
  }

  const entry: MountedWidget = {
    root,
    widgetRoot,
    listeners,
    setOpenSignal: (value) => {
      openSignal = value
      render()
    },
    setCloseSignal: (value) => {
      closeSignal = value
      render()
    },
  }

  mounted.set(element, entry)
  render()

  const destroy = () => unmount(element)

  const handle = (() => destroy()) as WidgetHandle
  handle.open = () => entry.setOpenSignal(Date.now())
  handle.close = () => entry.setCloseSignal(Date.now())
  handle.destroy = destroy
  handle.on = (event, listener) => {
    const set = listeners.get(event) ?? new Set()
    set.add(listener)
    listeners.set(event, set)
    return () => set.delete(listener)
  }

  return handle
}

// ----- A customer's booking link (ACA-106) -----

/** The booking link view's own script, published beside this one. */
export const MANAGE_SCRIPT_FILE = 'booking-widget-manage.iife.js'
const MANAGE_GLOBAL = 'AutoChainBookingManage'
export const MANAGE_LOADING_TEXT = 'Loading your booking…'
export const MANAGE_FAILED_TEXT = "We couldn't load your booking. Check your connection and try again."

interface ManageScript {
  mountManage(target: HTMLElement, config: ManageBookingMountConfig): () => void
}

/**
 * Where the booking link view's script is: next to this bundle, wherever the
 * page loaded it from. Null when this bundle cannot tell where that was.
 */
export function manageScriptUrl(doc: Document = document): string | null {
  const own =
    ownScript?.src ||
    Array.from(doc.scripts).find((script) => /\/booking-widget\.iife\.js(?:[?#]|$)/.test(script.src))?.src
  return own ? new URL(MANAGE_SCRIPT_FILE, own).toString() : null
}

/**
 * Mounts the customer's "change or cancel your booking" view (ACA-106) into
 * an element. The view is its own script, so this loads it first and says so
 * in the element meanwhile; the hosted page at `/manage/{token}` loads that
 * script itself and never comes here. Returns a function that unmounts it.
 */
export function mountManage(target: HTMLElement, config: ManageBookingMountConfig): () => void {
  if (mounted.has(target)) unmount(target)

  const status = document.createElement('p')
  status.setAttribute('role', 'status')
  status.textContent = MANAGE_LOADING_TEXT
  target.appendChild(status)

  let stopped = false
  let unmountView: (() => void) | null = null
  const src = manageScriptUrl()
  const loading = src
    ? loadGlobalScript<ManageScript>(document, window, src, MANAGE_GLOBAL)
    : Promise.reject(new Error('The booking widget script address is unknown'))

  loading
    .then((script) => {
      if (stopped) return
      status.remove()
      unmountView = script.mountManage(target, config)
    })
    .catch((error) => {
      if (stopped) return
      status.textContent = MANAGE_FAILED_TEXT
      console.error('AutoChain booking widget: could not load the booking link view', error)
    })

  return () => {
    stopped = true
    status.remove()
    unmountView?.()
    unmountView = null
  }
}

/** Unmounts a previously mounted widget. */
export function unmount(target: string | HTMLElement): void {
  const element = resolveElement(target)
  if (!element) return

  const entry = mounted.get(element)
  if (!entry) return

  entry.root.unmount()
  entry.widgetRoot.dispose()
  entry.listeners.clear()
  mounted.delete(element)

  for (const [selector, mappedElement] of selectorToElement.entries()) {
    if (mappedElement === element) selectorToElement.delete(selector)
  }
}

/**
 * Mounts without needing a host element — injects its own container.
 * The easiest embed, and the one the installation snippet uses.
 */
export function init(
  config: BookingWidgetConfig,
  options?: { containerId?: string }
): WidgetHandle {
  const containerId = options?.containerId ?? 'ac-booking-widget-root'

  let container = document.getElementById(containerId)
  if (container && !(container instanceof HTMLElement)) {
    throw new Error(`BookingWidget: #${containerId} exists but is not an element`)
  }

  if (!container) {
    container = document.createElement('div')
    container.id = containerId
    document.body.appendChild(container)
  }

  return mount(container, config)
}

// ----- Declarative auto-mount (CSP-friendly, no inline script) -----

function normalizeApiUrl(raw: string): string | null {
  try {
    const url = new URL(raw)
    // Never downgrade a secure page to a plaintext API call.
    if (window.location.protocol === 'https:' && url.protocol !== 'https:') return null
    return url.toString()
  } catch {
    return null
  }
}

/**
 * `data-autochain-return-url`: the exact page to come back to after paying,
 * for a widget a site builder puts inside a frame (ACA-111). A web address
 * only, relative ones read against this page; anything else is ignored. The
 * server still only uses it when it is on one of the channel's websites.
 */
export function parseReturnUrlAttribute(raw: string | null): string | undefined {
  const value = raw?.trim()
  if (!value) return undefined
  try {
    const url = new URL(value, window.location.href)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : undefined
  } catch {
    return undefined
  }
}

function parseAutoMountConfig(element: HTMLElement): BookingWidgetConfig | null {
  const apiUrlRaw = element.getAttribute('data-autochain-api-url')
  const publicKeyRaw = element.getAttribute('data-autochain-public-key')
  if (!apiUrlRaw || !publicKeyRaw) return null

  const apiUrl = normalizeApiUrl(apiUrlRaw)
  if (!apiUrl) return null

  const publicKey = publicKeyRaw.trim()
  if (!publicKey) return null

  const displayModeRaw = element.getAttribute('data-autochain-display-mode')
  const flowTypeRaw = element.getAttribute('data-autochain-flow')
  const triggerSelector = element.getAttribute('data-autochain-trigger') ?? undefined
  const launcherRaw = element.getAttribute('data-autochain-launcher')

  return {
    apiUrl,
    publicKey,
    garageId: element.getAttribute('data-autochain-garage-id') ?? undefined,
    displayMode: isDisplayMode(displayModeRaw) ? displayModeRaw : undefined,
    flowType: isFlowType(flowTypeRaw) ? flowTypeRaw : undefined,
    triggerSelector,
    launcher: launcherRaw === null ? undefined : launcherRaw !== 'false',
    returnUrl: parseReturnUrlAttribute(element.getAttribute('data-autochain-return-url')),
  }
}

function autoMountFromDom(): void {
  document.querySelectorAll(AUTO_MOUNT_SELECTOR).forEach((node) => {
    if (!(node instanceof HTMLElement) || autoMounted.has(node)) return

    const config = parseAutoMountConfig(node)
    if (!config) {
      console.error('BookingWidget: auto-mount skipped, missing or invalid data attributes')
      return
    }

    try {
      mount(node, config)
      autoMounted.add(node)
    } catch (error) {
      console.error('BookingWidget: auto-mount failed', error)
    }
  })
}

// ----- One-line embed: the script tag configures itself -----

/** The production API. A staging page overrides it with data-autochain-api-url. */
export const DEFAULT_API_URL = 'https://app.autochain.co.uk'

const SCRIPT_KEY_ATTRIBUTE = 'data-autochain-key'
const SCRIPT_MOUNT_FLAG = '__autochainBookingWidgetScriptMount'

type FlaggedWindow = Window & { [SCRIPT_MOUNT_FLAG]?: string }

/**
 * Read synchronously while this bundle is executing: `document.currentScript`
 * is only set during that first run, including for `async` and `defer`
 * scripts and for a tag a tag manager injects later.
 */
const ownScript =
  typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null

function hasAutochainAttributes(script: HTMLScriptElement): boolean {
  return script.getAttributeNames().some((name) => name.startsWith('data-autochain-'))
}

/**
 * Config from `<script src=".../booking-widget.iife.js" data-autochain-key="pk_...">`.
 * Null when the tag has no key: the March snippet calls init() itself.
 */
export function parseScriptTagConfig(script: HTMLScriptElement): BookingWidgetConfig | null {
  const publicKey = script.getAttribute(SCRIPT_KEY_ATTRIBUTE)?.trim()
  if (!publicKey) return null

  const apiUrlRaw = script.getAttribute('data-autochain-api-url')
  const apiUrl = apiUrlRaw ? normalizeApiUrl(apiUrlRaw) : DEFAULT_API_URL
  if (!apiUrl) return null

  const triggerSelector = script.getAttribute('data-autochain-trigger')?.trim() || undefined
  const buttonRaw = script.getAttribute('data-autochain-button')
  const displayModeRaw = script.getAttribute('data-autochain-display-mode')
  const flowTypeRaw = script.getAttribute('data-autochain-flow')

  return {
    apiUrl,
    publicKey,
    triggerSelector,
    launcher: buttonRaw === null ? undefined : buttonRaw.trim().toLowerCase() !== 'false',
    displayMode: isDisplayMode(displayModeRaw) ? displayModeRaw : undefined,
    flowType: isFlowType(flowTypeRaw) ? flowTypeRaw : undefined,
    returnUrl: parseReturnUrlAttribute(script.getAttribute('data-autochain-return-url')),
  }
}

/**
 * Mounts the widget a script tag asks for. One per page: pasting the line
 * twice, or loading the bundle twice, never shows two widgets.
 */
function mountFromScriptTag(): void {
  const candidates = [...(ownScript ? [ownScript] : []), ...Array.from(document.scripts)]
  const script =
    candidates.find((candidate) => candidate.hasAttribute(SCRIPT_KEY_ATTRIBUTE)) ??
    candidates.find(hasAutochainAttributes)
  if (!script) return

  const config = parseScriptTagConfig(script)
  if (!config) {
    console.error(
      'AutoChain booking widget: add your widget key to the script tag, for example ' +
        `${SCRIPT_KEY_ATTRIBUTE}="pk_...". You can copy the line from AutoChain, Settings, Booking widget.`
    )
    return
  }

  const flagged = window as FlaggedWindow
  if (flagged[SCRIPT_MOUNT_FLAG]) return
  flagged[SCRIPT_MOUNT_FLAG] = config.publicKey

  try {
    init(config)
  } catch (error) {
    delete flagged[SCRIPT_MOUNT_FLAG]
    console.error('AutoChain booking widget: could not start', error)
  }
}

function mountDeclaredWidgets(): void {
  autoMountFromDom()
  mountFromScriptTag()
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountDeclaredWidgets, { once: true })
  } else {
    mountDeclaredWidgets()
  }
}
